import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { orderBy } from 'lodash';
import moment from 'moment/moment';
import { type Scenario, Security, Value, WaterfallService } from 'api';
import { CapTableDetail } from 'pages/Waterfall/types';
import { useResponse } from 'services/hooks';
import { ISO_DATE_FORMAT, Logger, toString } from 'utilities';
import {
  UseWaterfallDistributionParams,
  UseWaterfallDistributionResult,
} from './useWaterfallDistribution.props';
import { WeightedShareValue } from '../../components/FundFirmDistribution/FundFirmDistribution.props';
import { calculateOwnersSharesValue, remapFundOwnershipToTargetSecurities } from '../../utilities';

export type { UseWaterfallDistributionParams, UseWaterfallDistributionResult } from './useWaterfallDistribution.props';

export const useWaterfallDistribution = ({
  selectedCaptable,
  equityValue,
  exitDate,
  captableDetail,
  fundOwnershipCapTableDetail,
  capTableList,
  isInitializedFromUrl,
  isInitializingFromUrl,
  selectedCompanyId,
  fundList,
  childrenCompanies,
  childrenCapTables,
  validSecurities,
}: UseWaterfallDistributionParams): UseWaterfallDistributionResult => {
  const [scenarios, setScenarios] = useState<Scenario>();
  const [isLoading, setIsLoading] = useState(false);
  const [updatedWeightedShareValues, setUpdatedWeightedShareValues] = useState<WeightedShareValue[] | undefined>();

  const { errorNotification } = useResponse();
  const lastDistributionParams = useRef<string>('');
  const previousCompanyId = useRef<number | undefined>(undefined);
  const forceRefresh = useRef<boolean>(false);

  // Initialize previous company ID on first render
  useEffect(() => {
    if (selectedCompanyId && previousCompanyId.current === undefined) {
      previousCompanyId.current = selectedCompanyId;
    }
  }, [selectedCompanyId]);

  // Remap FO source onto selected CT securities once per CT pair (not per distribution run).
  // When the FO source IS the selected cap table (a pro forma with its own FO), no remap is
  // needed -- the securities are already the same rows.
  const fundOwnershipForShares = useMemo(() => {
    if (!fundOwnershipCapTableDetail?.fund_ownership || !captableDetail) {
      return null;
    }
    if (fundOwnershipCapTableDetail.id === captableDetail.id) {
      return fundOwnershipCapTableDetail.fund_ownership;
    }
    return remapFundOwnershipToTargetSecurities(
      fundOwnershipCapTableDetail.fund_ownership,
      fundOwnershipCapTableDetail.securities,
      captableDetail.securities
    );
  }, [fundOwnershipCapTableDetail, captableDetail]);

  // ============================================================
  // Weighted Share Values Calculation
  // ============================================================
  const calculateWeightedShareValues = useCallback(
    (presentValues: Array<{ security: Security; value: number | string }>) => {
      if (!captableDetail || !childrenCompanies || !fundList) {
        return;
      }

      // Wait until the FO source has resolved; then require it to have FO (Valuations
      // pattern). If the FO source is missing/empty, set [] so the empty-state CTA renders.
      if (!fundOwnershipCapTableDetail) {
        return;
      }

      const sourceFundOwnership = fundOwnershipCapTableDetail.fund_ownership;
      const sourceFoDetails = sourceFundOwnership?.fund_ownership_detail ?? [];
      if (!sourceFundOwnership || sourceFoDetails.length === 0) {
        setUpdatedWeightedShareValues([]);
        return;
      }

      // Remap failed (rename / missing / ambiguous security names) — do not understate
      // concluded values or show the empty-FO CTA.
      if (!fundOwnershipForShares) {
        errorNotification(
          'Could not map fund ownership onto the selected cap table. Securities may have been renamed or duplicated across versions.'
        );
        setUpdatedWeightedShareValues(undefined);
        return;
      }

      const sortedPresentValues = orderBy(presentValues, ['security.id'], ['desc']);

      const ownersSharesValues = calculateOwnersSharesValue({
        sortedPresentValues: sortedPresentValues as Value[],
        capTableDetail: captableDetail as CapTableDetail,
        fundList,
        childrenCompanies,
        childrenCapTables,
        mainFundOwnershipRef: { current: fundOwnershipForShares },
        entityReferenceId: null,
      });

      setUpdatedWeightedShareValues(prev => {
        // Avoid a new array reference when content is unchanged — new refs rebuild all
        // FundInvestment sheets and can loop with workbook evaluation.
        if (
          prev &&
          prev.length === ownersSharesValues.length &&
          prev.every((item, index) => {
            const next = ownersSharesValues[index];
            return (
              item.fund.id === next.fund.id &&
              item.fund.name === next.fund.name &&
              item.owner === next.owner &&
              item.share_values.length === next.share_values.length &&
              item.share_values.every((sv, svIndex) => {
                const nextSv = next.share_values[svIndex];
                return (
                  sv.securityId === nextSv.securityId &&
                  sv.shares === nextSv.shares &&
                  sv.per_share === nextSv.per_share
                );
              })
            );
          })
        ) {
          return prev;
        }
        return ownersSharesValues;
      });
    },
    [
      captableDetail,
      fundOwnershipCapTableDetail,
      fundOwnershipForShares,
      childrenCompanies,
      fundList,
      childrenCapTables,
      errorNotification,
    ]
  );

  // Initialize weighted share values with zero values when dependencies are ready
  useEffect(() => {
    if (
      captableDetail &&
      fundOwnershipCapTableDetail &&
      childrenCompanies &&
      fundList &&
      validSecurities.length > 0
    ) {
      const defaultPresentValues = validSecurities.map(security => ({
        security: { name: security.name, id: security.id as number },
        value: 0,
      }));

      calculateWeightedShareValues(defaultPresentValues);
    }
  }, [
    captableDetail,
    fundOwnershipCapTableDetail,
    childrenCompanies,
    fundList,
    validSecurities,
    calculateWeightedShareValues,
  ]);

  // ============================================================
  // Distribution API Call
  // ============================================================
  const getDistribution = useCallback(async () => {
    // Do not proceed if the cap table is empty
    if (capTableList?.find(captable => captable.id === selectedCaptable)?.is_empty) {
      setScenarios(undefined);
      return;
    }

    const exitDateString = moment(exitDate).format(ISO_DATE_FORMAT);
    if (!selectedCaptable || !equityValue || !exitDateString) {
      setScenarios(undefined);
      return;
    }

    // Create a unique key for this distribution request to prevent duplicate calls
    // Include company ID to prevent cross-company conflicts
    const distributionKey = `${selectedCompanyId}-${selectedCaptable}-${equityValue}-${exitDateString}`;

    // Prevent duplicate calls with the same parameters, unless we're forcing a refresh
    if (lastDistributionParams.current === distributionKey && !forceRefresh.current) {
      return;
    }

    // Reset force refresh flag after using it
    forceRefresh.current = false;

    lastDistributionParams.current = distributionKey;

    try {
      setIsLoading(true);
      setScenarios(undefined);

      // `/api/waterfall` always runs liquidation waterfall math. Only
      // captableId, exitDate, and equityValue affect the calculation.
      // scenarioMethod / scenarioType are required by the codegen type but
      // ignored by WaterfallAllocationResultsSerializer.
      const scenarioValues = await WaterfallService.apiWaterfallCreate({
        captableId: selectedCaptable,
        equityValue: toString(equityValue),
        exitDate: exitDateString,
        scenarioMethod: 0,
        scenarioType: 1,
      });

      if (scenarioValues) {
        setScenarios(scenarioValues);
        // Calculate weighted share values from scenario results
        const { present_values: presentValues } = scenarioValues;
        if (presentValues) {
          calculateWeightedShareValues(presentValues as Array<{ security: { id: number, name: string }; value: string }>);
        }
      } else {
        setScenarios(undefined);
      }
    } catch (error: any) {
      let errorMessage = '';

      if (error.response?.text) {
        try {
          const errorMessageData = JSON.parse(error.response.text) as { [key: string]: string[] };
          // If it exists, error.response.text is an object with the actual message inside of an array
          const firstValue = Object.values(errorMessageData)[0];
          errorMessage = firstValue?.[0] ? firstValue[0] : error.response.statusText;
        } catch (parseError) {
          // If JSON parsing fails, use the fallback message
          errorMessage = 'An error occurred while processing the response';
          Logger.error('Error processing response', parseError);
        }
      }
      errorNotification(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [selectedCaptable, equityValue, exitDate, capTableList, calculateWeightedShareValues, errorNotification, selectedCompanyId]);

  // Reset distribution parameters when company changes
  useEffect(() => {
    if (selectedCompanyId && selectedCompanyId !== previousCompanyId.current) {
      previousCompanyId.current = selectedCompanyId;
      // Reset the distribution parameters to ensure fresh calculations for new company
      lastDistributionParams.current = '';
      setScenarios(undefined);
      forceRefresh.current = true; // Force refresh on company change
    }
  }, [selectedCompanyId]);

  // Also reset when cap table changes (in case it's a different company's cap table)
  useEffect(() => {
    if (selectedCaptable) {
      // Reset distribution parameters when cap table changes to ensure fresh calculations
      lastDistributionParams.current = '';
      setScenarios(undefined);
      forceRefresh.current = true; // Force refresh on cap table change
    }
  }, [selectedCaptable]);

  // Consolidated effect to handle distribution calls
  useEffect(() => {
    const shouldRunDistribution =
      captableDetail &&
      equityValue &&
      exitDate &&
      selectedCaptable &&
      (isInitializedFromUrl || !isInitializingFromUrl);

    if (shouldRunDistribution) {
      // Force refresh to ensure we always get fresh calculations
      forceRefresh.current = true;
      getDistribution();
    }
  }, [
    captableDetail,
    equityValue,
    exitDate,
    selectedCaptable,
    selectedCompanyId,
    isInitializedFromUrl,
    isInitializingFromUrl,
    getDistribution,
  ]);

  return {
    scenarios,
    setScenarios,
    isLoading,
    setIsLoading,
    getDistribution,
    updatedWeightedShareValues,
  };
};
